import React, { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from 'react-intl';
// Actions
import {
    clearRedux,
    accountTagsOpenViewEntitiesModal,
    setDriversEntity,
    sendGenericExport
} from "Redux/actions";
// Components
import { Grid, Container } from "@mui/material";
import { Table, SearchInput, Toolbar, Button } from 'Components/uiControls';
import Pill from 'Components/Pill';
import { TableButtons } from 'Components/uiControls/Table';
import { usePagination } from 'Components/uiControls/Table/hooks';
import { NotificationManager } from 'Components/Notifications';
import DeleteDriversTag from './components/deleteDriversTag';
import ViewEntitiesTag from 'Modules/account/views/tags/components/viewEntities';
import { HasPermission } from "Core/security"
import { searchDriversTags } from "./redux/slice";
import { openDriversTagDeleteModal }  from "./redux/slice";


const TagsTable = () => {
    const { messages, locale } = useIntl();
    // Hooks
    const dispatch = useDispatch();

    //Stale manager 
    const selectedEntity = useSelector(state => state.driverRedux.entity);
    const open = useSelector(state => state.driverTagsSliceRedux.openDriversTagDelete);

    const {
        tags,
        tagsTotal: total,
        loadingTags: loading,
        errorTags: error,
        tagsFilters: filters,
    } = useSelector(state => state.driverTagsSliceRedux);

    const openViewEntities = useSelector(state => state.viewEntitiesTagRedux.openViewEntities);

    const exportResult = useSelector(state => state.commomRedux.exportResult);
    const exportName = useSelector(state => state.commomRedux.exportName);
    const loadingExport = useSelector(state => state.commomRedux.loadingExport);
    const userSecurity = useSelector(state => state.securityRedux.user);

    // Permissions
    const editablePermissions = HasPermission(userSecurity, "", [342]);

    //Ref
    const tableRef = useRef();
    const pagination = usePagination(tableRef, searchDriversTags, filters);

    const entities = [
        {
            name: messages['settingsDriver_drivers'],
            id: "drivers",
        },
        {
            name: messages['settingsDriver_tags'],
            id: "tags",
        }
    ];

    if(HasPermission(userSecurity, ["fcscoll"]))
    {
        entities.push({
        name: messages['settingsDriver_driversFaces'],
        id: "driverFaces",
        })
    }

    useEffect(() => {
        pagination.setColumns([
            {
                key: 'label',
                title: messages['settingsDriver_tag'],
                show: true,
                export: true,
                render: (tag) => {
                    return (
                        <Pill
                            textField="label"
                            item={tag}
                            outlined={false}
                            style={{ margin: 5 }}
                        />
                    )
                },
                functionString: 'return row?.label'
            },
            {
                key: 'driversCount',
                title: messages['settingsDriver_drivers'],
                show: true,
                export: true,
                render: (tag) => {
                    return (
                        <span
                            className={`${tag?.disabled ? 'disabled' : ''} btn-link`}
                            style={{ marginLeft: 5, fontWeight: 'bold' }}
                            onClick={() => {
                                if (!tag?.disabled) {
                                    dispatch(accountTagsOpenViewEntitiesModal(true, tag, 2, true));
                                }
                            }}
                        >
                            {tag.driversCount || 0}
                        </span>
                    )
                },
                functionString: 'return row?.driversCount || 0'
            }
        ]);

 
    }, [locale]);

    useEffect(() => {
        if (exportResult && exportName === "driverTags") {
            NotificationManager.info(
                messages['exportInProcessBody'],
                messages['exportInProcessTitle'],
                4000,
                null,
                null,
                'info',
                null
            );
            dispatch(clearRedux("COMMON", { exportResult: null, exportName: null }));
        }
    }, [exportResult, exportName]);

    useEffect(() => {
        if (error) {
            NotificationManager.error(
                messages['errorMessage'],
                messages['error'],
                3000,
                null,
                null,
                'warning',
            );
            dispatch(clearRedux("SETTINGS_DRIVER", { errorTags: false }));
        }
    }, [error]);

    useEffect(() => {
        if (total) {
            pagination.setTotal(total);
        }
    }, [total]);

    useEffect(() => {
        pagination.onChangePage();
        return () => {
            dispatch(clearRedux("SETTINGS_DRIVER", {
                tags: [],
                tagsTotal: 0,
                errorTags: false,
                tagsFilters: {
                    openSearch: "",
                    limit: 0,
                    offset: 0
                }
            }));
        }
    }, []);

    return (
        <Container maxWidth={false}>
            <Grid container direction="row" justifyContent="space-between" alignItems="center">
                <Grid item xs={12} sm={12} md={4}>
                    <Toolbar>
                        <Grid
                            container
                            direction="row"
                            justifyContent="flex-start"
                            alignItems="center"
                        >
                            <Grid
                                item
                                style={{
                                    marginLeft: 6
                                }}
                            >

                                {
                                    editablePermissions && pagination?.selectedItems?.length > 0 &&
                                    <Button
                                        type="icon"
                                        icon="delete"
                                        placement="bottom"
                                        tooltip={messages['remove']}
                                        disabled={!pagination?.selectedItems?.length}
                                        onClick={() => {
                                            dispatch(openDriversTagDeleteModal({ open: true, entity: pagination?.selectedItems || [] }));
                                        }}
                                    />
                                }
                            </Grid>

                            <Grid item>
                                <TableButtons
                                    messages={messages}
                                    tableRef={tableRef}
                                    {...pagination}
                                    buttons={['export']}
                                    disabled={loading || loadingExport}
                                    onExport={(type) => {
                                        if (pagination?.selectedItems?.length) {
                                            if (type === "Csv") {
                                                tableRef.current.exportCSV();
                                            } else if (type === "Pdf") {
                                                tableRef.current.exportPDF();
                                            }

                                        } else {
                                            const columns = tableRef.current?.columns
                                            const headers = columns.filter(x => x.show && x.export).map(x => { return { header: x.title, column: x.key, functionString: x.functionString } }) || [];

                                            dispatch(sendGenericExport(
                                                "driverTags",
                                                type,
                                                `
                                                query searchTagsInline ($openSearch: String, $entityName: String!, $limit: Int, $offset: Int) {
                                                    Tags: searchTagsInline (openSearch: $openSearch, entityName: $entityName, limit: $limit, offset: $offset) {
                                                    items {
                                                            id
                                                            label
                                                            color
                                                            disabled
                                                            driversCount
                                                    }
                                                    total
                                                    }
                                                }
                                            `,
                                                {
                                                    openSearch: filters?.openSearch,
                                                    entityName: "Driver"
                                                },
                                                headers,
                                                { unit: 'pt', size: 'A4', orientation: 'landscape', fontSize: 10 }
                                            ));
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Toolbar>
                </Grid>

                <Grid item xs={12} sm={12} md={4} style={{ textAlign: 'center', marginBottom: 8 }}>
                    {entities.map((entity, index) => {
                        return (
                            <Pill
                                key={index}
                                textField={'name'}
                                item={entity}
                                outlined={selectedEntity?.id != entity?.id}
                                onClick={() => {
                                    dispatch(setDriversEntity(entity));
                                    pagination.setSelectedItems([]);
                                }}
                                style={{ border: '0' }}
                                label="pill-label-text"
                                colorField="default"
                                colorSelection="005bf8"
                            />
                        )
                    })}
                </Grid>

                <Grid item xs={12} sm={12} md={4} className="mb-10 text-right">
                    <SearchInput
                        placeholder={messages['settingsDriver_searchTags']}
                        {...pagination}
                    />
                </Grid>
            </Grid>

            <Table
                ref={tableRef}
                keyColumn="id"
                loading={loading}
                topheight={235}
                data={tags}
                exportOrientation='landscape'
                exportName='settings-drivers-tags'
                {...pagination}
                hideDragable
            />

            {
                open && <DeleteDriversTag callback={() => pagination?.resetTable()} />
            }

            {
                openViewEntities && <ViewEntitiesTag />
            }
        </Container>
    );
}

export default TagsTable;